'use client'

import {
  useRef,
  useState,
  FC,
  HTMLProps,
  JSX,
  Ref,
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect
} from 'react'
import {
  autoUpdate,
  useDismiss,
  useFloating,
  useId,
  useInteractions,
  useListNavigation,
  useMergeRefs,
  useRole
} from '@floating-ui/react'

import {
  Callout,
  CalloutText,
  Input,
  InputWrapper,
  Skeleton,
  Stack,
  Button,
  cn
} from 'design-system'

import { Check, Search, Trash2 } from 'lucide-react'

interface SearchSelectItemProps extends HTMLProps<HTMLDivElement> {
  children: ReactNode
  active: boolean
  selected: boolean
  ref?: Ref<HTMLDivElement>
}

const SearchSelectItem: FC<SearchSelectItemProps> = ({
  children,
  active,
  selected,
  ref,
  ...rest
}) => {
  return (
    <div
      ref={ref}
      role="option"
      aria-selected={selected}
      aria-current={active ? 'location' : undefined}
      {...rest}
      className={cn(
        'relative flex shrink-0 cursor-pointer select-none items-center justify-between gap-x-2 rounded-lg p-2 text-sm outline-none transition-colors hover:bg-blue-100 focus:bg-blue-100 focus:outline-none',
        {
          'bg-gray-100': selected,
          'bg-blue-100': active
        }
      )}>
      <div className="flex-1 truncate pr-2">{children}</div>
      {selected ? <Check size={16} aria-hidden className="shrink-0" /> : null}
    </div>
  )
}

type SearchSelectProps<T> = {
  items: T[]
  loading: boolean
  onSelect(item: T): void
  filterPredicate(item: T, inputValue: string): boolean
  placeholder: string
  renderItem(item: T): JSX.Element
  itemsName: string
  selectedId?: string
  onSearch?(query: string): void
  expandOnFocus?: boolean
  label?: string
  labelId?: string
  className?: string
  disabled?: boolean
  error?: Error | null
  clearable?: boolean
  onClear?: () => void
  displayValue?: string
  ariaLabel?: string
}

export function SearchSelect<ItemType extends { id: string }>({
  items,
  loading,
  onSelect,
  filterPredicate,
  placeholder,
  renderItem,
  selectedId,
  itemsName,
  onSearch,
  expandOnFocus = true,
  label,
  labelId,
  className,
  disabled = false,
  error,
  clearable = false,
  onClear,
  displayValue,
  ariaLabel
}: SearchSelectProps<ItemType>) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  const [hasUserInput, setHasUserInput] = useState(false)

  const listRef = useRef<Array<HTMLElement | null>>([])
  const listboxId = useId()
  const statusId = useId()
  const fallbackInputId = useId()
  const resolvedInputId = labelId ?? (label ? fallbackInputId : undefined)

  const { refs, context } = useFloating<HTMLInputElement>({
    whileElementsMounted: autoUpdate,
    open: isExpanded
  })

  const role = useRole(context, { role: 'listbox' })
  const dismiss = useDismiss(context)
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [role, dismiss, listNav]
  )

  const ref = useMergeRefs([refs.setReference, inputRef])

  const handleItemClick = useCallback(
    (itemId: string) => {
      if (itemId !== selectedId) {
        const selectedItem = items.find(item => item.id === itemId)!
        onSelect(selectedItem)
        setAnnouncement(`${selectedItem.id} selected`)
      }
      setIsExpanded(false)
      setInputValue('')
      setHasUserInput(false)
      inputRef.current?.focus()
    },
    [selectedId, onSelect, items]
  )

  function onChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value
    setInputValue(value)
    setHasUserInput(true)

    if (value) {
      setActiveIndex(0)
      setIsExpanded(true)
      setAnnouncement(`${filteredItems.length} ${itemsName} found`)
    } else {
      setIsExpanded(false)
      setAnnouncement('')
      // Clear selection when user manually clears input
      if (onClear) {
        onClear()
      }
    }

    if (onSearch) {
      onSearch(value)
    }
  }

  function onFocus() {
    if (expandOnFocus && !disabled) {
      setIsExpanded(true)
      if (filteredItems.length > 0) {
        setAnnouncement(`${filteredItems.length} ${itemsName} available`)
      }
    }
  }

  function onBlur() {
    setTimeout(() => {
      setIsExpanded(false)
    }, 150)
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (
      event.key === 'Enter' &&
      activeIndex !== null &&
      filteredItems[activeIndex]
    ) {
      if (filteredItems[activeIndex].id !== selectedId && !loading) {
        onSelect(filteredItems[activeIndex])
        setIsExpanded(false)
        setInputValue('')
        setHasUserInput(false)
        setAnnouncement(`${filteredItems[activeIndex].id} selected`)
      }
    }
    if (event.key === 'Escape') {
      setIsExpanded(false)
      setAnnouncement('List closed')
    }
  }

  function handleClear() {
    setInputValue('')
    setHasUserInput(false)
    setAnnouncement('Selection cleared')
    if (onClear) {
      onClear()
    }
    inputRef.current?.focus()
  }

  const filteredItems = items.filter(item => filterPredicate(item, inputValue))

  useEffect(() => {
    if (loading) {
      setActiveIndex(null)
      return
    }
    if (isExpanded && filteredItems.length > 0) {
      const timer = setTimeout(() => setActiveIndex(0), 30)
      return () => clearTimeout(timer)
    }
  }, [loading, isExpanded, filteredItems.length])

  if (error) {
    return (
      <div className="text-red-600" role="alert">
        Sorry we can&apos;t load {itemsName}, try to refresh the page
      </div>
    )
  }

  const emptyState = (
    <Callout calloutSize="sm">
      <CalloutText calloutSize="sm">No {itemsName} found</CalloutText>
    </Callout>
  )

  const loadingState = (
    <Stack gap={2}>
      <Skeleton className="h-9 rounded-lg" />
      <Skeleton className="h-9 rounded-lg" />
      <Skeleton className="h-9 rounded-lg" />
    </Stack>
  )

  const renderContent = () => {
    if (loading) {
      return loadingState
    }
    if (filteredItems.length > 0) {
      return filteredItems.map((item, index) => (
        <SearchSelectItem
          key={`search-select-item-${item.id}`}
          id={`option-${item.id}`}
          {...getItemProps({
            ref(node) {
              listRef.current[index] = node
            },
            onClick: () => handleItemClick(item.id)
          })}
          active={activeIndex === index}
          selected={item.id === selectedId}>
          {renderItem(item)}
        </SearchSelectItem>
      ))
    }
    return emptyState
  }

  const selectedItem = items.find(item => item.id === selectedId)
  const showClearButton = clearable && (selectedItem || inputValue.length > 0)

  return (
    <div className={cn('relative', className)}>
      <Stack gap={4}>
        {label && (
          <label
            htmlFor={resolvedInputId}
            className="text-sm font-medium text-gray-700">
            {label}
          </label>
        )}

        <InputWrapper
          leftIconSlot={<Search />}
          rightActionSlot={
            showClearButton ? (
              <Button
                buttonType="transparent"
                buttonSize="xs"
                onClick={handleClear}
                className="hover:text-red-500"
                leftIconSlot={<Trash2 />}
                aria-label="Clear selection"
              />
            ) : undefined
          }>
          <Input
            {...getReferenceProps({
              ref,
              onChange,
              onFocus,
              onBlur,
              onKeyDown,
              value: hasUserInput ? inputValue : displayValue || ''
            })}
            type="text"
            placeholder={placeholder}
            role="combobox"
            aria-label={ariaLabel}
            aria-expanded={isExpanded}
            aria-haspopup="listbox"
            aria-controls={isExpanded ? listboxId : undefined}
            aria-activedescendant={
              isExpanded && activeIndex !== null && filteredItems[activeIndex]
                ? `option-${filteredItems[activeIndex].id}`
                : undefined
            }
            aria-describedby={statusId}
            disabled={disabled || loading}
            id={resolvedInputId}
          />
        </InputWrapper>

        {/* Live region for status announcements */}
        <div
          id={statusId}
          aria-live="polite"
          aria-atomic="true"
          className="sr-only">
          {announcement}
        </div>

        {isExpanded && (
          <div
            {...getFloatingProps({
              ref: refs.setFloating
            })}
            id={listboxId}
            role="listbox"
            aria-label={`${itemsName} options`}
            className="absolute top-full z-50 flex max-h-48 w-full flex-col gap-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) =>
              e.preventDefault()
            }>
            {renderContent()}
          </div>
        )}
      </Stack>
    </div>
  )
}
